/**
 * Where things go and what they are, loaded once and shared by everything that
 * has to offer a choice.
 *
 * Locations and categories are the two lists a capture touches on every single
 * item, so they are fetched together, kept in one place, and written back by
 * the same hook that reads them -- both routes answer with the whole list after
 * a create, which is one round trip instead of two on the path somebody walks
 * fifty times in a sitting.
 *
 * **Recent picks live here too.** The brief asks for them by name and the
 * reason is speed: in a real basement the next item is almost always in the
 * same bin as the last one, so the right answer is usually one tap rather than
 * a search. They are per device, in localStorage, because they are a
 * convenience rather than data -- losing them costs one extra tap.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { locationTree, type CategoryCount, type LocationCount, type LocationNode } from "@iltocat/shared";
import { api } from "./api.js";

const RECENT_LOCATIONS = "iltocat.recent.locations";
const RECENT_CATEGORIES = "iltocat.recent.categories";
const KEEP_RECENT = 5;

export interface Taxonomy {
  locations: LocationCount[];
  categories: CategoryCount[];
  /** The same locations as a tree, with counts rolled up. */
  tree: LocationNode[];
  byId: Map<string, LocationNode>;
  loading: boolean;
  /** Most recently used first, and only ones that still exist. */
  recentLocations: LocationCount[];
  recentCategories: CategoryCount[];
  addLocation: (name: string, parentId: string | null) => Promise<LocationCount | null>;
  /** Rename it, move it (null is the top level), or both. Throws the route's sentence on a refusal. */
  updateLocation: (id: string, change: { name?: string; parentId?: string | null }) => Promise<void>;
  /** Deletes it; whatever was inside moves up a level. */
  /** `into`: where its contents go -- a location, null for the top level, left out for its parent. */
  deleteLocation: (id: string, into?: string | null) => Promise<void>;
  addCategory: (name: string) => Promise<CategoryCount | null>;
  /** Called on save, so the next capture offers the bin this one went into. */
  remember: (locationId: string | null, categoryId: string | null) => void;
  reload: () => Promise<void>;
}

export function useTaxonomy(): Taxonomy {
  const [locations, setLocations] = useState<LocationCount[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);
  // Kept in state as well as in storage so that using a location re-orders the
  // picker immediately, without a reload and without reading storage on render.
  const [recent, setRecent] = useState(() => ({
    locations: readRecent(RECENT_LOCATIONS),
    categories: readRecent(RECENT_CATEGORIES),
  }));

  const reload = useCallback(async () => {
    try {
      const [places, kinds] = await Promise.all([api.locations.list(), api.categories.list()]);
      setLocations(places.locations);
      setCategories(kinds.categories);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const tree = useMemo(() => locationTree(locations), [locations]);
  const byId = useMemo(() => {
    const map = new Map<string, LocationNode>();
    const walk = (nodes: LocationNode[]) => {
      for (const node of nodes) {
        map.set(node.id, node);
        walk(node.children);
      }
    };
    walk(tree);
    return map;
  }, [tree]);

  const addLocation = useCallback(
    async (name: string, parentId: string | null): Promise<LocationCount | null> => {
      const { locations: next } = await api.locations.create(name, parentId);
      setLocations(next);
      // The route answers with the list rather than the node, so the new one is
      // found the same way a test finds it: by name *and* parent, because the
      // same name under two parents is two different places.
      return (
        next.find(
          (location) =>
            location.name.toLowerCase() === name.trim().replace(/\s+/g, " ").toLowerCase() &&
            location.parentId === parentId,
        ) ?? null
      );
    },
    [],
  );

  const updateLocation = useCallback(
    async (id: string, change: { name?: string; parentId?: string | null }): Promise<void> => {
      setLocations((await api.locations.update(id, change)).locations);
    },
    [],
  );

  const deleteLocation = useCallback(async (id: string, into?: string | null): Promise<void> => {
    setLocations((await api.locations.remove(id, into)).locations);
  }, []);

  const addCategory = useCallback(async (name: string): Promise<CategoryCount | null> => {
    const { categories: next } = await api.categories.create(name);
    setCategories(next);
    return next.find((category) => category.name.toLowerCase() === name.trim().toLowerCase()) ?? null;
  }, []);

  const remember = useCallback((locationId: string | null, categoryId: string | null) => {
    setRecent((current) => ({
      locations: locationId
        ? writeRecent(RECENT_LOCATIONS, locationId, current.locations)
        : current.locations,
      categories: categoryId
        ? writeRecent(RECENT_CATEGORIES, categoryId, current.categories)
        : current.categories,
    }));
  }, []);

  const recentLocations = useMemo(() => resolve(recent.locations, locations), [recent.locations, locations]);
  const recentCategories = useMemo(
    () => resolve(recent.categories, categories),
    [recent.categories, categories],
  );

  return {
    locations,
    categories,
    tree,
    byId,
    loading,
    recentLocations,
    recentCategories,
    addLocation,
    updateLocation,
    deleteLocation,
    addCategory,
    remember,
    reload,
  };
}

/* ------------------------------------------------------------- plumbing -- */

/** Ids to things, dropping any that have since been deleted. A picker never offers a ghost. */
const resolve = <T extends { id: string }>(ids: string[], all: T[]): T[] =>
  ids.map((id) => all.find((entry) => entry.id === id)).filter((entry): entry is T => Boolean(entry));

function readRecent(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeRecent(key: string, id: string, current: string[]): string[] {
  const next = [id, ...current.filter((existing) => existing !== id)].slice(0, KEEP_RECENT);
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Storage being unavailable costs a shortcut, not a save.
  }
  return next;
}
